// src/monitoring/components/primitives.jsx
//
// The visual grammar of the Monitoring section: a stat tile, the park status
// chip, the alarm severity chip, the "simulated" badge and the number
// formatters. One file so every page reads the same.

import React from "react";
import { Box, Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { useLanguage } from "../../context/LanguageContext";
import { useMonitoringText } from "../i18n";

export const fmtNum = (v, digits = 1, locale = "el-GR") =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? "—"
    : new Intl.NumberFormat("en-GB", { maximumFractionDigits: digits, minimumFractionDigits: digits }).format(Number(v));

export const fmtKw = (v, locale) => (v == null ? "—" : Number(v) >= 10000 ? `${fmtNum(v / 1000, 2, locale)} MW` : `${fmtNum(v, Number(v) >= 100 ? 0 : 1, locale)} kW`);
export const fmtKwh = (v, locale) => (v == null ? "—" : Number(v) >= 10000 ? `${fmtNum(v / 1000, 1, locale)} MWh` : `${fmtNum(v, 0, locale)} kWh`);
export const fmtKwp = (v, locale) => (v == null ? "—" : Number(v) >= 10000 ? `${fmtNum(v / 1000, 2, locale)} MWp` : `${fmtNum(v, 0, locale)} kWp`);
export const fmtPct = (v, digits = 0, locale) => (v == null ? "—" : `${fmtNum(Number(v) * 100, digits, locale)} %`);

export function fmtTime(instant, locale = "el-GR", tz = "Europe/Athens") {
  if (!instant) return "—";
  return new Intl.DateTimeFormat(locale, { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(instant));
}
export function fmtDateTime(instant, locale = "el-GR", tz = "Europe/Athens") {
  if (!instant) return "—";
  return new Intl.DateTimeFormat(locale, { timeZone: tz, day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(instant));
}
export function fmtDate(day, locale = "el-GR") {
  if (!day) return "—";
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" }).format(new Date(`${day}T00:00:00`));
}

export function useLocale() {
  const { locale } = useLanguage();
  return locale || "el-GR";
}

export const STATUS_COLOR = {
  ok: "success", warning: "warning", critical: "error", stale: "default", no_data: "default", not_wired: "info", disabled: "default",
};
export const STATUS_HEX = {
  ok: "#2e7d32", warning: "#ed6c02", critical: "#d32f2f", stale: "#9e9e9e", no_data: "#9e9e9e", not_wired: "#0288d1", disabled: "#9e9e9e",
};

export function StatusChip({ status, size = "small" }) {
  const t = useMonitoringText();
  const key = status || "no_data";
  return <Chip size={size} color={STATUS_COLOR[key] || "default"} variant={key === "ok" ? "outlined" : "filled"} label={t(`status.${key}`)} />;
}

export const SEVERITY_COLOR = { info: "info", warning: "warning", critical: "error" };

export function SeverityChip({ severity, size = "small" }) {
  const t = useMonitoringText();
  return <Chip size={size} color={SEVERITY_COLOR[severity] || "default"} label={t(`severity.${severity}`)} />;
}

export function SimulatedBadge({ sx }) {
  const t = useMonitoringText();
  return (
    <Tooltip title={t("shell.simulatedHint")} arrow>
      <Chip size="small" color="warning" variant="outlined" icon={<ScienceOutlinedIcon fontSize="inherit" />} label={t("shell.simulated")} sx={sx} />
    </Tooltip>
  );
}

export function ReplayBadge({ sx }) {
  const t = useMonitoringText();
  return (
    <Tooltip title={t("shell.replayHint")} arrow>
      <Chip size="small" color="info" variant="outlined" icon={<ReplayOutlinedIcon fontSize="inherit" />} label={t("shell.replay")} sx={sx} />
    </Tooltip>
  );
}

export function StatTile({ label, value, sub, accent, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0, height: "100%", ...sx }}>
      <Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.4, display: "block" }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: accent || "text.primary", lineHeight: 1.15, overflowWrap: "anywhere" }}>{value}</Typography>
      {sub && <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{sub}</Typography>}
      {children}
    </Paper>
  );
}

export function SectionTitle({ title, hint, action }) {
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
        {hint && <Typography variant="body2" sx={{ color: "text.secondary" }}>{hint}</Typography>}
      </Box>
      {action}
    </Stack>
  );
}

/** Colour for an inverter's relative performance (1 = park median). */
export function relativeColor(rel) {
  if (rel == null) return "#bdbdbd";
  if (rel >= 0.95) return "#2e7d32";
  if (rel >= 0.85) return "#9ccc65";
  if (rel >= 0.7) return "#ed6c02";
  return "#d32f2f";
}
